import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    title: "Gold, honest to the karat",
    body: "We work in 9K, 14K, 18K and 22K gold — each clearly labelled, each BIS hallmarked. The karat tells you exactly how much pure gold is in the alloy: 22K is 91.6% pure, 18K is 75%, 14K is 58.5%, 9K is 37.5%. Lower karats trade purity for hardness, which is why everyday rings often wear 14K or 18K best.",
    points: ["BIS hallmark on every gold piece", "Net metal weight disclosed per variant", "Purity priced off the live 24K rate — no blended opacity"],
  },
  {
    title: "Silver, stamped 925",
    body: "Our silver is sterling — 92.5% pure silver, the balance copper for strength. Every piece is stamped 925. Oxidised finishes are surface treatments over the same sterling base; they wear in, not off, developing character over years.",
    points: ["925 sterling throughout", "Oxidised and polished finishes disclosed per piece", "Fixed MRP pricing — silver prices are not rate-linked"],
  },
  {
    title: "Stones, graded and disclosed",
    body: "Diamond and gemstone weights are stated in carats on the product page and the invoice. Where stones are set, the metal weight you pay for is net of stone weight — you never pay gold rates for stone grams.",
    points: ["Carat weight on page and invoice", "Metal weight is net of stones", "Stone type disclosed — natural, lab-grown or simulant, never ambiguous"],
  },
  {
    title: "Stone disclosure — treatment, stated on every piece",
    body: "Most coloured gemstones in the market are treated — heat, irradiation, dyeing, stabilisation — and treatment changes a stone's value. There is nothing wrong with a treated stone; there is everything wrong with not saying so. Siyaara states the species, origin (natural, lab-grown or simulant) and treatment of every gemstone in its specification table, in plain words — for example, 'Natural blue topaz, irradiated (standard industry treatment)'. Where a stone carries a traditional association, we label it as tradition and belief — offered for interest, never a promise of any outcome.",
    points: ["Species, origin and treatment on every gemstone's spec table — a product cannot publish without them", "Treatment in plain words, including when 'unknown' is the honest answer", "A simulant is never described as natural; cubic zirconia is cubic zirconia, never 'diamond'"],
  },
  {
    title: "Made to order, made to last",
    body: "Most Siyaara pieces are crafted after you order, in 7–10 days. That means no old stock, no inventory games, and a piece that is yours alone. It also means we measure twice: every piece is weighed after crafting and any variance beyond tolerance is settled back to you.",
    points: ["7–10 day crafting window", "Post-crafting weight check with variance settlement", "Lifetime exchange and buyback at prevailing rates"],
  },
];

export default function Materials() {
  return (
    <div className="container-x py-12 max-w-4xl" data-testid="materials-page">
      <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">Materials & Quality</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">What your jewellery is actually made of</h1>
      <p className="text-stone-600 mt-3 max-w-2xl">
        No vague "gold-plated" language, no hidden alloys. Every material, purity and weight is disclosed —
        because the only way to trust a price is to see what's inside it.
      </p>
      <div className="space-y-6 mt-10">
        {SECTIONS.map((s, i) => (
          <section key={s.title} className="border border-stone-200 bg-white p-6 sm:p-8" data-testid={`materials-section-${i}`}>
            <h2 className="font-heading text-xl font-semibold">{s.title}</h2>
            <p className="text-sm text-stone-600 mt-2 leading-relaxed">{s.body}</p>
            <ul className="mt-4 space-y-1.5">
              {s.points.map((p) => (
                <li key={p} className="text-xs text-stone-600 flex items-start gap-2">
                  <span className="text-gold mt-0.5">◆</span>{p}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      <div className="mt-10 bg-brand text-ivory p-8 text-center">
        <h2 className="font-heading text-2xl font-semibold">See the breakup before you pay</h2>
        <p className="text-sm text-ivory/70 mt-2">Metal, making and GST — itemised on every product page and every invoice.</p>
        <Link to="/shop"><Button className="mt-5 bg-gold text-brand hover:bg-ivory btn-press rounded-full px-8" data-testid="materials-shop-cta">Shop with transparency</Button></Link>
      </div>
    </div>
  );
}
